(function () {
  'use strict';

  var SELECTOR = '.product-card__ksp';
  var SCROLL_PX_PER_SEC = 14;
  var PAUSE_MS = 900;
  var HOVER_DELAY_MS = 80;
  var TOUCH_ENABLE_RETRIES = 8;
  var TOUCH_ENABLE_INTERVAL = 80;

  var instances = new WeakMap();
  var cardMap = new WeakMap();
  var activeScrollers = new Set();
  var globalRafId = null;
  var activeMarqueeLi = null;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function useMobileMode() {
    return window.matchMedia('(hover: none), (max-width: 989px)').matches;
  }

  function useHoverScrollMode() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches && !useMobileMode();
  }

  function findCard(ksp) {
    return ksp.closest('.product-card') || ksp.closest('.hr-card');
  }

  function canScroll(ksp) {
    return ksp.scrollHeight > ksp.clientHeight + 1;
  }

  function stopMarquee(li) {
    if (!li) return;
    var inner = li.querySelector('.ksp-li__inner');
    li.classList.remove('ksp-li--active', 'ksp-li--playing');
    if (inner) {
      inner.style.transform = '';
      inner.removeEventListener('animationend', li._kspAnimEnd);
    }
    if (activeMarqueeLi === li) activeMarqueeLi = null;
  }

  function playMarqueeOnce(li) {
    if (!li || !li.querySelector('.ksp-li__scroll--overflow')) return;

    if (activeMarqueeLi && activeMarqueeLi !== li) stopMarquee(activeMarqueeLi);

    var inner = li.querySelector('.ksp-li__inner');
    if (!inner) return;

    stopMarquee(li);
    activeMarqueeLi = li;
    li.classList.add('ksp-li--active', 'ksp-li--playing');

    li._kspAnimEnd = function (e) {
      if (e.animationName !== 'ksp-li-marquee-once') return;
      stopMarquee(li);
    };
    inner.addEventListener('animationend', li._kspAnimEnd);
  }

  function measureKspLi(li) {
    var scroll = li.querySelector('.ksp-li__scroll');
    var inner = li.querySelector('.ksp-li__inner');
    if (!scroll || !inner) return;

    if (!li.classList.contains('ksp-li--playing')) {
      inner.style.transform = '';
    }
    inner.style.removeProperty('--ksp-li-shift');
    inner.style.removeProperty('--ksp-li-duration');
    scroll.classList.remove('ksp-li__scroll--overflow');
    li.classList.remove('ksp-li--overflow');

    requestAnimationFrame(function () {
      var shift = inner.scrollWidth - scroll.clientWidth;
      if (shift > 1) {
        scroll.classList.add('ksp-li__scroll--overflow');
        li.classList.add('ksp-li--overflow');
        inner.style.setProperty('--ksp-li-shift', shift + 'px');
        inner.style.setProperty('--ksp-li-duration', Math.max(3, shift / 24) + 's');
      }
    });
  }

  function extractKspLiParts(li) {
    var icon = li.querySelector(':scope > img, :scope > svg, :scope > picture');
    var labelEl = li.querySelector('strong, b, .eg-ksp__label, .eg-ksp_label');
    var descEl = li.querySelector('.eg-ksp__desc, .eg-ksp_desc');
    var label = '';
    var desc = '';

    if (labelEl && descEl) {
      label = labelEl.textContent.trim();
      desc = descEl.textContent.trim();
    } else if (labelEl) {
      label = labelEl.textContent.trim();
      var clone = li.cloneNode(true);
      if (icon) {
        var iconClone = clone.querySelector('img, svg, picture');
        if (iconClone) iconClone.remove();
      }
      clone.querySelectorAll('strong, b, .eg-ksp__label, .eg-ksp_label').forEach(function (el) {
        el.remove();
      });
      clone.querySelectorAll('.ksp-li__scroll, .ksp-li__body, .ksp-li__label, .ksp-li__arrow').forEach(function (el) {
        el.remove();
      });
      desc = clone.textContent.trim();
    } else {
      var plain = li.cloneNode(true);
      if (icon) {
        var iconInPlain = plain.querySelector('img, svg, picture');
        if (iconInPlain) iconInPlain.remove();
      }
      plain.querySelectorAll('.ksp-li__scroll, .ksp-li__body, .ksp-li__label, .ksp-li__arrow').forEach(function (el) {
        el.remove();
      });
      desc = plain.textContent.trim();
    }

    return { icon: icon, label: label, desc: desc };
  }

  function bindKspLiEvents(li, inner) {
    /* 桌面：悬停滚动（持续） */
    li.addEventListener('mouseenter', function () {
      if (useMobileMode()) return;
      li.classList.add('ksp-li--active');
    });
    li.addEventListener('mouseleave', function () {
      if (useMobileMode()) return;
      li.classList.remove('ksp-li--active');
      inner.style.transform = '';
    });

    /* 手机：点击 / 点箭头 → 滚完一遍自动停 */
    function onActivate(e) {
      if (!useMobileMode()) return;
      if (!li.classList.contains('ksp-li--overflow')) return;
      e.preventDefault();
      e.stopPropagation();
      if (li.classList.contains('ksp-li--playing')) {
        stopMarquee(li);
        return;
      }
      playMarqueeOnce(li);
    }

    li.addEventListener('click', onActivate);
    var arrow = li.querySelector('.ksp-li__arrow');
    if (arrow) {
      arrow.addEventListener('click', onActivate);
    }
  }

  function buildKspLi(li) {
    var parts = extractKspLiParts(li);
    if (!parts.desc && !parts.label) return null;

    while (li.firstChild) li.removeChild(li.firstChild);
    if (parts.icon) li.appendChild(parts.icon);

    if (parts.label) {
      var labelSpan = document.createElement('span');
      labelSpan.className = 'ksp-li__label';
      labelSpan.textContent = parts.label;
      li.appendChild(labelSpan);
      li.classList.add('ksp-li--has-label');
    }

    var body = document.createElement('span');
    body.className = 'ksp-li__body';
    var scroll = document.createElement('span');
    scroll.className = 'ksp-li__scroll';
    var inner = document.createElement('span');
    inner.className = 'ksp-li__inner';
    inner.textContent = parts.desc || parts.label;

    scroll.appendChild(inner);
    body.appendChild(scroll);
    li.appendChild(body);

    var arrow = document.createElement('button');
    arrow.type = 'button';
    arrow.className = 'ksp-li__arrow';
    arrow.setAttribute('aria-label', 'Show more');
    arrow.innerHTML =
      '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    li.appendChild(arrow);

    bindKspLiEvents(li, inner);
    return inner;
  }

  function initKspListItems(ksp) {
    ksp.querySelectorAll('ul li').forEach(function (li) {
      if (li.dataset.kspLiReady !== 'true') {
        li.dataset.kspLiReady = 'true';
        buildKspLi(li);
      }
      measureKspLi(li);
    });
  }

  function closeOtherCards(currentCard) {
    document.querySelectorAll('.is-ksp-touch').forEach(function (el) {
      if (el !== currentCard) {
        el.classList.remove('is-ksp-touch');
        var other = cardMap.get(el);
        if (other) other.disable();
      }
    });
  }

  function globalTick(now) {
    globalRafId = null;
    activeScrollers.forEach(function (state) {
      state.step(now);
    });
    if (activeScrollers.size > 0) {
      globalRafId = requestAnimationFrame(globalTick);
    }
  }

  function ensureGlobalLoop() {
    if (!globalRafId && activeScrollers.size > 0) {
      globalRafId = requestAnimationFrame(globalTick);
    }
  }

  function createInstance(ksp) {
    var paused = false;
    var pauseUntil = 0;
    var direction = 1;
    var active = false;
    var card = findCard(ksp);
    var hoverTimer = null;
    var enableRetryTimer = null;
    var scrollFloat = 0;
    var lastStepTime = 0;
    var manualControl = false;
    var mobileOnceDone = false;
    var mobilePhase = 'down'; /* down → up → done */
    var ioVisible = false;

    var state = {
      ksp: ksp,
      card: card,
      manualControl: false,

      isRevealOpen: function () {
        if (useHoverScrollMode()) return !!(card && card.matches(':hover'));
        if (useMobileMode()) return ioVisible && !mobileOnceDone;
        return ioVisible;
      },

      clearEnableRetry: function () {
        if (enableRetryTimer) {
          clearTimeout(enableRetryTimer);
          enableRetryTimer = null;
        }
      },

      scheduleEnable: function (retryLeft) {
        var self = this;
        this.clearEnableRetry();
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            self.refresh();
            if (!active && self.isRevealOpen() && retryLeft > 0) {
              enableRetryTimer = setTimeout(function () {
                self.scheduleEnable(retryLeft - 1);
              }, TOUCH_ENABLE_INTERVAL);
            }
          });
        });
      },

      stop: function () {
        active = false;
        activeScrollers.delete(state);
      },

      resetScroll: function () {
        ksp.scrollTop = 0;
        scrollFloat = 0;
        lastStepTime = 0;
        direction = 1;
        pauseUntil = 0;
        mobilePhase = 'down';
      },

      enable: function () {
        if (!this.isRevealOpen() || !canScroll(ksp)) {
          ksp.classList.remove('product-card__ksp--autoscroll');
          this.stop();
          return;
        }

        if (active) {
          if (useHoverScrollMode() && ksp.matches(':hover')) this.enterManualMode();
          return;
        }

        /* 从顶部开始滚；离开时不立刻 reset，避免淡出时列表跳动 */
        this.resetScroll();
        ksp.classList.add('product-card__ksp--autoscroll');
        active = true;
        activeScrollers.add(state);
        ensureGlobalLoop();
        if (useHoverScrollMode() && ksp.matches(':hover')) this.enterManualMode();
      },

      disable: function (keepPosition) {
        ksp.classList.remove('product-card__ksp--autoscroll', 'product-card__ksp--manual');
        this.manualControl = false;
        manualControl = false;
        paused = false;
        this.stop();
        if (!keepPosition) this.resetScroll();
      },

      finishMobileOnce: function () {
        mobileOnceDone = true;
        mobilePhase = 'done';
        ksp.classList.remove('product-card__ksp--autoscroll');
        this.stop();
        ksp.scrollTop = 0;
        scrollFloat = 0;
      },

      enterManualMode: function () {
        if (useMobileMode() || !active) return;
        this.manualControl = true;
        manualControl = true;
        paused = true;
        lastStepTime = 0;
        scrollFloat = ksp.scrollTop;
        ksp.classList.add('product-card__ksp--manual');
      },

      exitManualMode: function () {
        if (!this.manualControl) return;
        this.manualControl = false;
        manualControl = false;
        paused = false;
        scrollFloat = ksp.scrollTop;
        ksp.classList.remove('product-card__ksp--manual');
        if (active && this.isRevealOpen()) ensureGlobalLoop();
      },

      step: function (now) {
        if (
          !active ||
          paused ||
          manualControl ||
          prefersReducedMotion() ||
          !canScroll(ksp) ||
          !this.isRevealOpen()
        ) {
          if (useHoverScrollMode() && !this.isRevealOpen()) this.disable(true);
          else lastStepTime = 0;
          return;
        }

        if (now < pauseUntil) {
          lastStepTime = 0;
          return;
        }

        if (!lastStepTime) lastStepTime = now;
        var delta = Math.min(now - lastStepTime, 48);
        lastStepTime = now;

        var max = ksp.scrollHeight - ksp.clientHeight;
        scrollFloat = Math.max(0, Math.min(max, scrollFloat + direction * (SCROLL_PX_PER_SEC * delta) / 1000));
        ksp.scrollTop = scrollFloat;

        if (useMobileMode()) {
          /* 手机：向下 → 停顿 → 向上回顶 → 结束（只一轮） */
          if (mobilePhase === 'down' && scrollFloat >= max - 0.5) {
            scrollFloat = max;
            ksp.scrollTop = max;
            direction = -1;
            mobilePhase = 'up';
            pauseUntil = now + PAUSE_MS;
            lastStepTime = 0;
          } else if (mobilePhase === 'up' && scrollFloat <= 0.5) {
            this.finishMobileOnce();
          }
          return;
        }

        /* 桌面：来回循环 */
        if (direction === 1 && scrollFloat >= max - 0.5) {
          scrollFloat = max;
          ksp.scrollTop = max;
          direction = -1;
          pauseUntil = now + 1800;
          lastStepTime = 0;
        } else if (direction === -1 && scrollFloat <= 0.5) {
          scrollFloat = 0;
          ksp.scrollTop = 0;
          direction = 1;
          pauseUntil = now + 1800;
          lastStepTime = 0;
        }
      },

      refresh: function () {
        if (this.isRevealOpen()) this.enable();
        else if (useHoverScrollMode()) this.disable(true);
        else if (useMobileMode() && mobileOnceDone) {
          /* 已完成一次：保持可见位置，不再自动滚 */
          this.disable(true);
        } else if (!ioVisible) {
          this.disable();
        }
      },
    };

    var track = ksp.closest('[data-hr-track]');
    var ioOptions = track ? { root: track, threshold: 0.25 } : { threshold: 0.2 };

    var io = new IntersectionObserver(function (entries) {
      ioVisible = entries[0] && entries[0].isIntersecting;

      if (useHoverScrollMode()) return;

      if (useMobileMode()) {
        if (ioVisible && !mobileOnceDone) {
          initKspListItems(ksp);
          state.scheduleEnable(TOUCH_ENABLE_RETRIES);
        } else if (!ioVisible && !mobileOnceDone) {
          state.disable();
        }
        return;
      }

      if (ioVisible) state.enable();
      else state.disable();
    }, ioOptions);
    io.observe(ksp);

    var lastRoSize = 0;
    var ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(function (entries) {
            var nextSize = ksp.clientHeight;
            if (Math.abs(nextSize - lastRoSize) < 1) return;
            lastRoSize = nextSize;
            initKspListItems(ksp);
            if (state.isRevealOpen()) state.scheduleEnable(2);
          })
        : null;
    if (ro) ro.observe(ksp);

    initKspListItems(ksp);

    ksp.addEventListener('transitionend', function (e) {
      if (
        (e.propertyName === 'opacity' || e.propertyName === 'max-height') &&
        state.isRevealOpen()
      ) {
        initKspListItems(ksp);
        state.scheduleEnable(2);
      }
    });

    ksp.addEventListener('mouseenter', function () {
      if (useMobileMode()) return;
      state.enterManualMode();
    });

    ksp.addEventListener('mouseleave', function () {
      if (useMobileMode()) return;
      state.exitManualMode();
    });

    ksp.addEventListener(
      'scroll',
      function () {
        if (manualControl) scrollFloat = ksp.scrollTop;
      },
      { passive: true }
    );

    if (card) {
      cardMap.set(card, state);
    }

    if (card && useHoverScrollMode()) {
      card.addEventListener('mouseenter', function () {
        closeOtherCards(card);
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () {
          state.scheduleEnable(TOUCH_ENABLE_RETRIES);
        }, HOVER_DELAY_MS);
      });
      card.addEventListener('mouseleave', function () {
        clearTimeout(hoverTimer);
        state.clearEnableRetry();
        state.exitManualMode();
        /* keepPosition：淡出期间保持 scrollTop，避免列表回顶造成抖动 */
        state.disable(true);
      });
    }

    /* 手机端：初始就在视口内则启动一次滚动 */
    if (useMobileMode()) {
      requestAnimationFrame(function () {
        var rect = ksp.getBoundingClientRect();
        var inView = rect.top < window.innerHeight * 0.9 && rect.bottom > window.innerHeight * 0.1;
        if (inView && !mobileOnceDone) {
          ioVisible = true;
          state.scheduleEnable(TOUCH_ENABLE_RETRIES);
        }
      });
    }

    return {
      state: state,
      refresh: function () {
        state.refresh();
      },
      destroy: function () {
        clearTimeout(hoverTimer);
        state.clearEnableRetry();
        state.exitManualMode();
        state.disable();
        io.disconnect();
        if (ro) ro.disconnect();
        if (card) {
          card.classList.remove('is-ksp-touch');
          cardMap.delete(card);
        }
      },
    };
  }

  function init(root) {
    root.querySelectorAll(SELECTOR).forEach(function (ksp) {
      initKspListItems(ksp);
    });

    if (prefersReducedMotion()) return;

    root.querySelectorAll(SELECTOR).forEach(function (ksp) {
      if (!instances.has(ksp)) {
        instances.set(ksp, createInstance(ksp));
      } else {
        instances.get(ksp).refresh();
      }
    });
  }

  function boot() {
    init(document);
  }

  var reinitTimer = null;
  function scheduleInit(root) {
    clearTimeout(reinitTimer);
    reinitTimer = setTimeout(function () {
      init(root || document);
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  /* Mega 菜单等通过 Section Rendering API 异步注入产品卡 */
  document.addEventListener('sectionsRenderingAPI:cached', function () {
    scheduleInit(document);
  });

  /* 兜底：replaceWith 注入 DOM 后也能初始化（含 cache 路径无事件的情况） */
  if (typeof MutationObserver !== 'undefined') {
    var moTimer = null;
    var mo = new MutationObserver(function (mutations) {
      var needInit = false;
      for (var i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (node.nodeType !== 1) continue;
          if (
            (node.matches && node.matches(SELECTOR)) ||
            (node.querySelector && node.querySelector(SELECTOR))
          ) {
            needInit = true;
            break;
          }
        }
        if (needInit) break;
      }
      if (!needInit) return;
      clearTimeout(moTimer);
      moTimer = setTimeout(function () {
        init(document);
      }, 80);
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', function () {
    if (prefersReducedMotion()) {
      activeScrollers.clear();
      if (globalRafId) {
        cancelAnimationFrame(globalRafId);
        globalRafId = null;
      }
      document.querySelectorAll(SELECTOR).forEach(function (el) {
        var inst = instances.get(el);
        if (inst) inst.destroy();
      });
    } else {
      boot();
    }
  });
})();
